"use client";

import { useEffect, useState } from "react";
import {
  getSavedRemedies,
  toggleRemedyCompleted,
  deleteRemedy,
  type SavedRemedyItem,
} from "@/lib/storage/remedy-diary";

interface ConsultationRemedyDiaryProps {
  isHindi?: boolean;
  className?: string;
}

export default function ConsultationRemedyDiary({
  isHindi = false,
  className = "",
}: ConsultationRemedyDiaryProps) {
  const [remedies, setRemedies] = useState<SavedRemedyItem[]>([]);

  useEffect(() => {
    const sync = () => setRemedies(getSavedRemedies());
    sync();
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);

  if (remedies.length === 0) return null;

  const handleToggle = (id: string) => {
    toggleRemedyCompleted(id);
    setRemedies(getSavedRemedies());
  };

  const handleDelete = (id: string) => {
    deleteRemedy(id);
    setRemedies(getSavedRemedies());
  };

  const CATEGORY_ICONS: Record<string, string> = {
    mantra: "📿",
    gemstone: "💎",
    pooja: "🔥",
    charity: "🤲",
    fasting: "🌙",
  };

  return (
    <div
      className={`my-6 rounded-2xl border border-border-default bg-surface-raised p-5 shadow-xs ${className}`}
    >
      <div className="flex items-center justify-between gap-3 pb-3 border-b border-border-default">
        <div>
          <span className="text-caption font-bold uppercase tracking-wider text-intent-brand">
            {isHindi ? "आपकी व्यक्तिगत वैदिक डायरी" : "Your Personal Vedic Diary"}
          </span>
          <h4 className="text-title font-serif font-bold text-text-primary mt-0.5">
            {isHindi ? "सहेजे गए ज्योतिषीय उपाय व मंत्र" : "Saved Astrological Remedies & Mantras"}
          </h4>
        </div>
        <span className="text-caption font-bold text-text-muted">
          {remedies.length} {isHindi ? "उपाय" : remedies.length === 1 ? "remedy" : "remedies"}
        </span>
      </div>

      <div className="divide-y divide-border-default mt-2">
        {remedies.map((item) => {
          const icon = CATEGORY_ICONS[item.category] || "✦";
          return (
            <div key={item.id} className="py-3.5 flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <button
                  type="button"
                  onClick={() => handleToggle(item.id)}
                  className={`mt-0.5 w-5 h-5 rounded-md border flex items-center justify-center transition-colors ${
                    item.isCompleted
                      ? "bg-status-success border-success text-text-on-brand"
                      : "border-border-default bg-surface-raised hover:border-intent-brand"
                  }`}
                  aria-label={item.isCompleted ? "Mark incomplete" : "Mark completed"}
                >
                  {item.isCompleted && <span className="text-caption">✓</span>}
                </button>

                <div>
                  <div className="flex items-center gap-2">
                    <span>{icon}</span>
                    <span
                      className={`text-body-small font-bold ${
                        item.isCompleted ? "line-through text-text-muted" : "text-text-primary"
                      }`}
                    >
                      {item.title}
                    </span>
                    <span className="text-caption text-intent-brand bg-surface-brand-subtle px-2 py-0.5 rounded-full">
                      {item.prescribedFor}
                    </span>
                  </div>
                  <p className="text-caption text-text-secondary mt-1">{item.description}</p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => handleDelete(item.id)}
                className="text-caption text-text-muted hover:text-error p-1 transition-colors"
                aria-label="Remove remedy"
              >
                ✕
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
